import { useState, type ReactNode } from 'react';
import { useTranslation } from '@mypoz/i18n';
import { Icon } from '@mypoz/ui';

import { SECURITY_CENTER_URL } from '@/lib/legacy-api.js';
import { buttonClass } from '@/lib/styles.js';
import { useAccountSession } from '../context.js';

export interface VerificationNoticeProps {
  /** What the customer cannot do yet, e.g. "Verify your identity to create orders". */
  title: string;
  /** Why, in one or two sentences. */
  body: string;
  /** The last of the three steps: what to come back and do once approved. */
  finalStep: string;
  /** The heading's level where the notice is placed: a page of its own, or inside a card. */
  heading?: 'h1' | 'h3';
  /** Below the buttons, e.g. a way back. */
  children?: ReactNode;
}

/**
 * "Verify your identity first" (MYP-142, MYP-109): why, the three steps on the old website's
 * Security Center — the new app never handles IC or face photos — and "check again".
 *
 * Shared by Create order (`VerificationGate`) and the China Payment request card, which the old
 * website also refuses to unverified customers ("未认证者，是无法充值的"). "Check again" re-reads
 * the account without blanking the page (`refresh`): once it says verified, whatever this notice
 * stands in for appears in its place.
 */
export function VerificationNotice({
  title,
  body,
  finalStep,
  heading = 'h1',
  children,
}: VerificationNoticeProps) {
  const { t } = useTranslation();
  const { refresh } = useAccountSession();
  const [checking, setChecking] = useState(false);
  // The answer to the last "check again": still not verified, or no answer at all.
  const [outcome, setOutcome] = useState<'stillNot' | 'failed' | null>(null);
  const Heading = heading;

  const checkAgain = async () => {
    setChecking(true);
    setOutcome(null);
    const answered = await refresh();
    // Still here after an answer means still not verified: once verified, the notice is replaced.
    // No answer (offline, a vendor error) must not be reported as "not verified".
    setChecking(false);
    setOutcome(answered ? 'stillNot' : 'failed');
  };

  return (
    <div className="grid gap-4">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-warning/10 text-warning"
        >
          <Icon name="badge" size={22} />
        </span>
        <div className="min-w-0">
          <Heading
            className={
              heading === 'h1'
                ? 'text-xl font-semibold text-ink'
                : 'text-base font-semibold text-ink'
            }
          >
            {title}
          </Heading>
          <p className="mt-1 text-sm text-muted">{body}</p>
        </div>
      </div>

      <ol className="grid list-decimal gap-1 pl-5 text-sm text-ink">
        <li>{t('account.verification.gate.step1')}</li>
        <li>{t('account.verification.gate.step2')}</li>
        <li>{finalStep}</li>
      </ol>

      <p className="text-sm text-muted">{t('account.verification.gate.pending')}</p>

      <div className="flex flex-wrap gap-3">
        <a href={SECURITY_CENTER_URL} className={buttonClass('primary', 'px-5')}>
          {t('account.verification.verifyOnMypoz')}
        </a>
        <button
          type="button"
          onClick={() => void checkAgain()}
          disabled={checking}
          aria-busy={checking || undefined}
          className={buttonClass('secondary', 'px-5')}
        >
          {checking
            ? t('account.verification.gate.checking')
            : t('account.verification.gate.checkAgain')}
        </button>
      </div>

      {/* Announced, so a screen reader hears the answer to "check again" too. */}
      <p
        role="status"
        className={`text-sm empty:hidden ${outcome === 'failed' ? 'text-danger' : 'text-warning'}`}
      >
        {outcome === 'stillNot'
          ? t('account.verification.gate.stillNot')
          : outcome === 'failed'
            ? t('account.verification.gate.checkFailed')
            : ''}
      </p>

      {children}
    </div>
  );
}
